import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@/features/auth/hooks/useSession';
import { useWorkspacePlanTier } from '@/features/billing/hooks/useWorkspacePlanTier';
import { MembersSection } from './MembersSection';
import { BillingSection } from './BillingSection';
import { ActivitySection } from './ActivitySection';
import './WorkspaceSettings.css';

type SettingsTab = 'members' | 'billing' | 'activity';

export function WorkspaceSettings() {
  const navigate = useNavigate();
  const { profile, selectedContainer, token } = useSession();
  const workspaceTier = useWorkspacePlanTier();
  const [activeTab, setActiveTab] = useState<SettingsTab>('members');

  const isOwner = selectedContainer?.isOwner !== false;
  const workspaceName = selectedContainer?.containerName || profile?.name || 'Workspace';

  if (!token || !selectedContainer?.id) {
    return <div className="ws-settings-empty">No workspace selected.</div>;
  }

  if (!isOwner) {
    return (
      <div className="ws-settings-empty">
        <p>Only workspace owners and admins can access settings.</p>
        <button className="ws-settings-back" onClick={() => navigate('/')}>Back to conversations</button>
      </div>
    );
  }

  return (
    <div className="ws-settings">
      <div className="ws-settings-header">
        <button className="ws-settings-back" onClick={() => navigate('/')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>
        <h1 className="ws-settings-title">{workspaceName}</h1>
        <span className="ws-settings-tier">{workspaceTier ?? 'free'}</span>
      </div>

      <nav className="ws-settings-nav">
        {(['members', 'billing', 'activity'] as const).map(tab => (
          <button
            key={tab}
            className={`ws-settings-nav-btn ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'members' ? 'Members' : tab === 'billing' ? 'Billing' : 'Activity'}
          </button>
        ))}
      </nav>

      <div className="ws-settings-content">
        {activeTab === 'members' && (
          <MembersSection workspaceId={selectedContainer.id} token={token} />
        )}
        {activeTab === 'billing' && (
          <BillingSection workspaceId={selectedContainer.id} token={token} tier={workspaceTier} />
        )}
        {activeTab === 'activity' && (
          <ActivitySection workspaceId={selectedContainer.id} />
        )}
      </div>
    </div>
  );
}
